import APIHandler from 'config/APIHandler';
import { storageKeys } from 'config/settings.json';
import StorageHandler from 'config/StorageHandler';

import { useQuery } from '@tanstack/react-query';

type CollectionData = Record<string, unknown[]>;

function getQueryKey(lastSegment: string, salesCompanionAppId: string | number | undefined) {
  return ['collection', lastSegment, salesCompanionAppId] as const;
}

function getQueryFn(lastSegment: string, salesCompanionAppId: string | number | undefined) {
  return async (): Promise<CollectionData> => {
    const { langFile: lang } = (await StorageHandler.load(storageKeys.lang)) as { langFile: string };
    const appId = import.meta.env.VITE_APP_ID;

    if (lastSegment === 'images') {
      const res = (await APIHandler.getImages(appId, lang)) as { items: CollectionData };
      return res.items;
    }

    if (lastSegment === 'videos') {
      const { items } = (await APIHandler.getVideos(appId, lang)) as {
        items: Record<string, { chapter: { chapterNumber: number } }[]>;
      };
      const newItems: CollectionData = {};
      Object.keys(items).forEach((key) => {
        const chapterArray = items[key];
        const firstItem = chapterArray?.[0];
        if (!firstItem) return;
        const { chapterNumber } = firstItem.chapter;
        newItems[chapterNumber] = chapterArray;
      });
      return newItems;
    }

    const res = (await APIHandler.getNamedCollection(
      lastSegment.replace(/---/g, '%2D').replace(/-/g, ' '),
      salesCompanionAppId ?? appId,
    )) as { items: CollectionData };
    return res.items;
  };
}

export function useCollectionData(lastSegment: string, salesCompanionAppId?: string | number) {
  return useQuery({
    queryKey: getQueryKey(lastSegment, salesCompanionAppId),
    queryFn: getQueryFn(lastSegment, salesCompanionAppId),
  });
}
